// src/ui/controls/PauseButton.tsx — D-02 in-game pause entry (07-UI-SPEC §3).
//
// Exact mirror of src/ui/controls/TorchButton.tsx: a HUD-corner icon button
// (top-left, mirrors TorchButton's bottom-right) that writes a one-way signal
// on the renderBus. Tap emits `pause-requested { source: 'menu' }` — the SAME
// sim-pause signal the visibilitychange path uses (07-PATTERNS.md / Pitfall D:
// do NOT invent a second pause loop; `source` only records who asked). The
// pause panel + dim and the resume path are wired in App.tsx in Plan 10; this
// is the STANDALONE entry control.
//
// onPointerDown + preventDefault (the TorchButton idiom) fires on touch contact
// before iOS click synthesis, so the emit lands exactly once per tap.
//
// ZERO new design tokens (07-UI-SPEC): 44px touch floor, cream stroke, amber
// pressed tint, z-index 80 HUD tier. Inline-style mirror of pause-button.css so
// jsdom specs assert layout without the stylesheet (TorchButton convention).
//
// SOURCE: 07-UI-SPEC.md §Surface 3; 07-PATTERNS.md §PauseButton.tsx.

import { useCallback, type JSX, type PointerEvent as ReactPointerEvent } from 'react';

import { renderBus } from '../../render/bus';

import './pause-button.css';

export function PauseButton(): JSX.Element {
  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      // preventDefault stops iOS from synthesising a click / focus ring and
      // double-firing a compatibility mousedown (TorchButton idiom).
      e.preventDefault();
      renderBus.emit('pause-requested', { source: 'menu' });
    },
    [],
  );

  return (
    <button
      type="button"
      className="pause-button"
      aria-label="Pause"
      data-testid="pause-button"
      onPointerDown={onPointerDown}
      // Inline-style mirror of pause-button.css for jsdom layout assertions.
      style={{
        position: 'fixed',
        top: 'calc(env(safe-area-inset-top) + 16px)',
        left: 'calc(env(safe-area-inset-left) + 16px)',
        width: '44px',
        height: '44px',
        borderRadius: '50%',
        border: '2px solid #2a4a62',
        background: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        touchAction: 'none',
        pointerEvents: 'auto',
        zIndex: 80,
        cursor: 'pointer',
        userSelect: 'none',
        outline: 'none',
        padding: 0,
        color: '#f0ead8',
      }}
    >
      {/* Inline SVG pause-bars pictograph — UI-SPEC: no external icon library.
          Cream stroke/fill in the default state; the amber pressed tint comes
          from .pause-button:active in pause-button.css. 24x24 viewBox centred
          inside the 44px button. */}
      <svg
        className="pause-button__icon"
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
        width="20"
        height="20"
      >
        <rect x="6" y="5" width="4" height="14" rx="1" fill="#f0ead8" />
        <rect x="14" y="5" width="4" height="14" rx="1" fill="#f0ead8" />
      </svg>
    </button>
  );
}
